/* src/ui/dom.ts — the hyperscript helper (PLAN.md Part 4 §2, TASKS.md 0.5).
 *
 * Every screen in the game is composed from el() calls. There is no template step and no
 * JSX, so the whole ui/ tree is plain .ts that Vite passes straight through — which is the
 * point: CLAUDE.md non-negotiable #1 forbids a UI framework, and a WebGL canvas already
 * owns the frame budget that a vdom diff would compete for.
 */

export type Child = Node | string | number | null | undefined | false | Child[];

export interface ElProps {
  class?: string;
  id?: string;
  style?: Partial<CSSStyleDeclaration>;
  /** Plain HTML attributes. `true` renders a bare boolean attribute, `false` omits it. */
  attrs?: Record<string, string | number | boolean>;
  /** Writes `data-*`. `data.action` is the delegation contract read by ui/dispatch.ts. */
  data?: Record<string, string>;
  /**
   * Per-node listener escape hatch. Deliberately rare: normal interaction goes through
   * `data.action` + the single root dispatcher, so N open windows cost one listener, not N.
   * Reserved for surfaces the delegator cannot serve (drag surfaces, `input`, `scroll`).
   */
  on?: Record<string, (e: Event) => void>;
}

function append(node: HTMLElement, child: Child): void {
  if (child === null || child === undefined || child === false) return;
  if (Array.isArray(child)) {
    for (const c of child) append(node, c);
    return;
  }
  node.appendChild(
    child instanceof Node ? child : document.createTextNode(String(child)),
  );
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props?: ElProps | null,
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  if (props) {
    if (props.class !== undefined) node.className = props.class;
    if (props.id !== undefined) node.id = props.id;
    if (props.style) Object.assign(node.style, props.style);

    if (props.attrs) {
      for (const [key, value] of Object.entries(props.attrs)) {
        if (value === false) continue;
        node.setAttribute(key, value === true ? '' : String(value));
      }
    }
    if (props.data) {
      for (const [key, value] of Object.entries(props.data)) {
        node.dataset[key] = value;
      }
    }
    if (props.on) {
      for (const [type, handler] of Object.entries(props.on)) {
        node.addEventListener(type, handler);
      }
    }
  }

  for (const child of children) append(node, child);
  return node;
}

/** Detach every child without touching the element itself. */
export function clear(node: HTMLElement): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

/**
 * A text node held by reference so a component can write to it directly.
 * This is the "hot binding" primitive: `node.textContent = x` is O(1) and allocation-free,
 * versus a diff that walks a tree on every change (PLAN.md Part 4 §2).
 */
export function text(initial = ''): Text {
  return document.createTextNode(initial);
}
